// btw-front/src/components/partner/signup/ExistingCompanyPicker.jsx
// 기존 회사 가입 — 이미 등록된 파트너사를 회사명으로 검색해 고른다.
// 로그인 전 화면이라 서버는 회사명·대표자명만 내려준다(사업자번호·연락처 비노출).
// 고른 뒤에는 회사명·대표자명을 읽기 전용으로 보여 주고, '다시 검색' 으로만 바꿀 수 있다.
// 실제 회사 값은 신청 시 서버가 고른 회사(partnerId)에서 복사한다 — 여기서 보낸 값은 쓰지 않는다.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../../ui/Input";
import { searchCompanies } from "../../../api/partnerSignup";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

export function ExistingCompanyPicker({ value, onChange, error }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  // 'idle' | 'searching' | 'done' | 'failed'
  const [status, setStatus] = useState("idle");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (value) return undefined;
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setStatus("idle");
      setItems([]);
      return undefined;
    }
    setStatus("searching");
    let cancelled = false;
    const handle = setTimeout(() => {
      searchCompanies(q)
        .then((rows) => {
          if (cancelled) return;
          setItems(rows);
          setStatus("done");
        })
        .catch(() => {
          if (cancelled) return;
          setItems([]);
          setStatus("failed");
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, value]);

  if (value) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label={t("partners.signup.member.companyLabel")}
          value={value.companyName}
          disabled
          inputClassName="disabled:bg-slate-100 disabled:text-slate-600"
        />
        <Input
          label={t("partners.signup.fields.representativeName")}
          value={value.representativeName || "-"}
          disabled
          inputClassName="disabled:bg-slate-100 disabled:text-slate-600"
        />
        <div className="flex flex-wrap items-center justify-between gap-2 md:col-span-2">
          <p className="text-xs text-slate-500">{t("partners.signup.member.selectedHint")}</p>
          <button type="button" className="btw-btn-ghost" onClick={() => onChange(null)}>
            {t("partners.signup.member.change")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <Input
        name="partnerId"
        label={t("partners.signup.member.searchLabel")}
        required
        placeholder={t("partners.signup.member.searchPlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        error={error}
        hint={t("partners.signup.member.searchHint")}
        maxLength={50}
        autoComplete="off"
      />
      {status === "searching" && (
        <p className="text-xs text-slate-500">{t("partners.signup.member.searching")}</p>
      )}
      {status === "failed" && (
        <p className="text-xs text-rose-600">{t("partners.signup.member.searchFailed")}</p>
      )}
      {status === "done" && items.length === 0 && (
        <p className="text-xs text-slate-500">{t("partners.signup.member.noResult")}</p>
      )}
      {status === "done" && items.length > 0 && (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition hover:bg-slate-50"
                onClick={() => onChange(c)}
              >
                <span className="font-medium text-slate-900">{c.companyName}</span>
                <span className="text-xs text-slate-500">
                  {t("partners.signup.fields.representativeName")} {c.representativeName || "-"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
